/**
 * The `#/document/{id}` hash route, with the id captured in group 1. Shared by the
 * validator (`.test`) and the id parser (`.match`) so the two can never diverge.
 */
const DOCUMENT_HASH_RE = /^#\/document\/([A-Za-z0-9._-]+)/;

/**
 * Validates that a string is a WorkDocs *document* URL, e.g.
 * `https://amazon.awsapps.com/workdocs-amazon/index.html#/document/{id}`.
 *
 * Requirements enforced:
 * - https on host `amazon.awsapps.com`
 * - a `#/document/{id}` fragment (folder links or other resource kinds are rejected)
 *
 * Both the `workdocs-amazon` and legacy `workdocs` path prefixes are accepted. Uses
 * the URL parser (not just a regex) so it is robust to query strings and casing.
 */
export const isWorkDocsDocumentUrl = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }

  if (url.protocol !== 'https:') return false;
  if (url.hostname.toLowerCase() !== 'amazon.awsapps.com') return false;
  if (!/^\/workdocs(-amazon)?\//i.test(url.pathname)) return false;

  // The document id lives in the hash route: #/document/{id}
  return DOCUMENT_HASH_RE.test(url.hash);
};

/**
 * Normalizes a pasted URL for storage and dedup comparison (trim only — WorkDocs
 * document ids are case-sensitive, so casing is preserved).
 */
export const normalizeWorkDocsUrl = (value: string): string => value.trim();

/**
 * Builds the WorkDocs web URL for a *folder* id, e.g.
 * `https://amazon.awsapps.com/workdocs-amazon/index.html#/folder/{id}`.
 *
 * Mirrors the document-URL shape (`#/document/{id}`) but on the `#/folder/{id}`
 * route — the link the baseball-card page surfaces as "Upload Data Here" so a
 * leader lands directly on their provisioned root folder.
 */
export const buildWorkDocsFolderUrl = (folderId: string): string => `https://amazon.awsapps.com/workdocs-amazon/index.html#/folder/${folderId}`;

/**
 * Extracts the WorkDocs document id from a document URL's `#/document/{id}`
 * fragment, or null if the URL isn't a valid WorkDocs document URL. The id is the
 * handle needed to fetch the document's content for preview/download.
 */
export const parseWorkDocsDocumentId = (value: string): string | null => {
  if (!isWorkDocsDocumentUrl(value)) return null;
  try {
    const { hash } = new URL(value.trim());
    const match = hash.match(DOCUMENT_HASH_RE);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};
